import { useAuth, useOrganization } from "@clerk/clerk-react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  hasOrgAccess,
  useBillingStatus,
} from "../components/BillingStatusContext";
import {
  type AssetScope,
  buildGalleryTree,
  enrichProjects,
  resolveSelectedNode,
} from "../components/gallery/galleryTree";
import {
  getGalleryTreeApiSessionsGalleryTreeGetQueryKey,
  listAssetsApiAssetsGetQueryKey,
  listProjectAssetsApiProjectsProjectIdAssetsGetOptions,
  listSessionsApiSessionsGetQueryKey,
} from "../generated/api/@tanstack/react-query.gen";
import {
  createProjectApiProjectsPost,
  moveSessionToProjectApiSessionsSessionIdProjectPatch,
  renameProjectApiProjectsProjectIdPatch,
  shareAssetWithOrgApiAssetsAssetIdShareWithOrgPost,
} from "../generated/api/sdk.gen";
import {
  exitSessionPath,
  selectAssetsPath,
  selectProjectPath,
  selectRootPath,
  sessionPath,
  startNewDesignPath,
} from "../lib/gallery-navigation";
import {
  gallerySessionsByScopeQuery,
  galleryTreeQuery,
  invalidateProjectAssets,
} from "../lib/queries";
import { useAssets } from "./useAssets";
import { useRenameSession } from "./useRenameSession";

/** The `/gallery` route search params the drawer resolves its selection from. */
interface GallerySearch {
  projectId: string | undefined;
  view: "assets" | undefined;
  scope: AssetScope | undefined;
}

function useVisibleSessions(
  visibleScope: { projectId: string | null },
  orgId: string | null | undefined,
) {
  const {
    data: visibleSessionsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isVisibleSessionsLoading,
    error: visibleSessionsError,
  } = useInfiniteQuery(gallerySessionsByScopeQuery(visibleScope, orgId));

  const visibleSessions = useMemo(
    () =>
      visibleSessionsData?.pages.flatMap((page) => page.sessions ?? []) ?? [],
    [visibleSessionsData],
  );

  return {
    visibleSessions,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isVisibleSessionsLoading,
    visibleSessionsError,
  };
}

function useGalleryTreeData(
  orgId: string | null | undefined,
  search: GallerySearch,
  canUseOrgFeatures: boolean,
) {
  const { projectId: selectedProjectId, view, scope } = search;
  const { data, isLoading, error } = useQuery({
    ...galleryTreeQuery(orgId),
    refetchOnMount: "always",
  });

  const projects = useMemo(() => data?.projects ?? [], [data?.projects]);
  const slimSessions = useMemo(() => data?.sessions ?? [], [data?.sessions]);
  const selectedNode = useMemo(
    () =>
      resolveSelectedNode(
        { projectId: selectedProjectId, view, scope },
        canUseOrgFeatures,
      ),
    [selectedProjectId, view, scope, canUseOrgFeatures],
  );
  const visibleScope = useMemo(
    () => ({
      projectId:
        selectedNode.type === "project" ? selectedNode.projectId : null,
    }),
    [selectedNode],
  );
  const treeData = useMemo(
    () => buildGalleryTree(projects, slimSessions),
    [projects, slimSessions],
  );
  const selectedProject =
    selectedNode.type === "project"
      ? projects.find((p) => p.id === selectedNode.projectId)
      : null;
  const enrichedProjects = useMemo(
    () => enrichProjects(selectedNode, treeData),
    [selectedNode, treeData],
  );
  const pagination = useVisibleSessions(visibleScope, orgId);

  return {
    isLoading,
    error,
    projects,
    slimSessions,
    selectedNode,
    selectedProject,
    orgRoot: treeData.orgRoot,
    rootProjects: treeData.projectsByParentId.get(null) ?? [],
    rootSessions: treeData.sessionsByProjectId.get(null) ?? [],
    treeData,
    enrichedProjects,
    hasGalleryContent: projects.length > 0 || slimSessions.length > 0,
    ...pagination,
  };
}

type NavigateFn = ReturnType<typeof useNavigate>;

interface GalleryHandlerDeps {
  navigate: NavigateFn;
  selectedProjectId: string | undefined;
  selectedNode: ReturnType<typeof resolveSelectedNode>;
  slimSessions: ReturnType<typeof useGalleryTreeData>["slimSessions"];
  createProjectMutation: {
    mutateAsync: (p: string | null) => Promise<unknown>;
  };
  renameProjectMutation: {
    mutateAsync: (p: { projectId: string; name: string }) => Promise<unknown>;
  };
  renameSession: (sessionId: string, name: string) => Promise<void>;
  moveSessionMutation: {
    mutateAsync: (p: {
      sessionId: string;
      projectId: string | null;
    }) => Promise<unknown>;
  };
  setExpandedProjectIds: React.Dispatch<React.SetStateAction<Set<string>>>;
}

function useGalleryHandlers({
  navigate,
  selectedProjectId,
  selectedNode,
  slimSessions,
  createProjectMutation,
  renameProjectMutation,
  renameSession,
  moveSessionMutation,
  setExpandedProjectIds,
}: GalleryHandlerDeps) {
  const selectProject = (projectId: string) =>
    navigate(selectProjectPath(projectId));
  const selectRoot = () => navigate(selectRootPath());
  const selectAssets = (scope: AssetScope = "personal") =>
    navigate(selectAssetsPath(scope));
  const exitSessionView = () => navigate(exitSessionPath(selectedProjectId));
  const handleStartNewDesign = () =>
    navigate(startNewDesignPath(selectedProjectId));
  const handleSessionClick = (sessionId: string) =>
    navigate(sessionPath(sessionId, slimSessions, selectedProjectId));
  const toggleProject = (projectId: string) => {
    setExpandedProjectIds((currentIds) => {
      const nextIds = new Set(currentIds);
      if (nextIds.has(projectId)) nextIds.delete(projectId);
      else nextIds.add(projectId);
      return nextIds;
    });
  };
  const handleCreateProject = async () => {
    const parentProjectId =
      selectedNode.type === "project" ? selectedNode.projectId : null;
    await createProjectMutation.mutateAsync(parentProjectId);
  };
  const handleRenameProject = async (
    projectId: string,
    name: string,
  ): Promise<void> => {
    await renameProjectMutation.mutateAsync({ projectId, name });
  };
  const handleMoveSessionToProject = async (
    sessionId: string,
    projectId: string | null,
  ): Promise<void> => {
    await moveSessionMutation.mutateAsync({ sessionId, projectId });
  };

  return {
    selectProject,
    selectRoot,
    selectAssets,
    exitSessionView,
    handleStartNewDesign,
    handleSessionClick,
    toggleProject,
    handleCreateProject,
    handleRenameProject,
    handleRenameSession: renameSession,
    handleMoveSessionToProject,
  };
}

function useMoveSessionMutation(invalidateGallery: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (p: { sessionId: string; projectId: string | null }) => {
      await moveSessionToProjectApiSessionsSessionIdProjectPatch({
        path: { session_id: p.sessionId },
        body: { project_id: p.projectId },
      });
    },
    onSuccess: async () => {
      invalidateGallery();
      // Moving a design changes the derived-used assets of both the source and
      // destination projects' sidebars, so refresh every project's asset list.
      await invalidateProjectAssets(queryClient);
    },
  });
}

function useGalleryMutations(
  invalidateGallery: () => void,
  navigate: NavigateFn,
  setIsRootExpanded: (v: boolean) => void,
  setExpandedProjectIds: React.Dispatch<React.SetStateAction<Set<string>>>,
) {
  const renameSession = useRenameSession();
  const createProjectMutation = useMutation({
    mutationFn: async (parentProjectId: string | null) => {
      const { data } = await createProjectApiProjectsPost({
        body: { parent_project_id: parentProjectId },
      });
      return data;
    },
    onSuccess: (project) => {
      invalidateGallery();
      navigate({ to: "/gallery", search: { projectId: project.id } });
      setIsRootExpanded(true);
      setExpandedProjectIds((currentIds) => {
        const nextIds = new Set(currentIds);
        nextIds.add(project.id);
        if (project.parent_project_id) nextIds.add(project.parent_project_id);
        return nextIds;
      });
    },
  });

  const renameProjectMutation = useMutation({
    mutationFn: async (p: { projectId: string; name: string }) => {
      const { data } = await renameProjectApiProjectsProjectIdPatch({
        path: { project_id: p.projectId },
        body: { name: p.name },
      });
      return data;
    },
    onSuccess: invalidateGallery,
  });

  return {
    createProjectMutation,
    renameProjectMutation,
    renameSession,
    moveSessionMutation: useMoveSessionMutation(invalidateGallery),
  };
}

function useAssetsSection(queryClient: ReturnType<typeof useQueryClient>) {
  // Centralises everything the gallery needs for the Assets sections:
  // the live list, refresh, and the share-to-org mutation. Keeps
  // useGalleryPage from sprawling past the lint length budget.
  const { organization } = useOrganization();
  const { billingStatus } = useBillingStatus();
  // Clerk auto-creates a personal org for every account, so an active org is
  // not an entitlement. Gate on the same rule the backend applies in
  // AuthService.get_active_org_context — otherwise free users see an empty
  // "Shared Projects" section. hasOrgAccess(null) is false, so the section
  // stays hidden while billing status loads rather than flashing into view.
  const canUseOrgFeatures = hasOrgAccess(billingStatus);
  const activeOrgName = canUseOrgFeatures ? (organization?.name ?? null) : null;
  const invalidateAssets = () =>
    queryClient.invalidateQueries({
      queryKey: listAssetsApiAssetsGetQueryKey(),
    });
  const shareAssetMutation = useMutation({
    mutationFn: async (assetId: string) => {
      await shareAssetWithOrgApiAssetsAssetIdShareWithOrgPost({
        path: { asset_id: assetId },
      });
    },
    onSuccess: invalidateAssets,
  });
  // Fire-and-forget: ``mutate`` (not ``mutateAsync``) so a failed share never
  // becomes an unhandled rejection in the drop handler. Failures surface via
  // ``shareAssetFailed`` and are captured by Sentry through react-query.
  const handleShareAssetWithOrg = (assetId: string) => {
    shareAssetMutation.mutate(assetId);
  };
  return {
    activeOrgName,
    canUseOrgFeatures,
    handleShareAssetWithOrg,
    shareAssetFailed: shareAssetMutation.isError,
  };
}

function useProjectAssets(
  selectedNode: ReturnType<typeof resolveSelectedNode>,
) {
  // Project Assets section: only fetched while a project node is selected.
  const isProjectNode = selectedNode.type === "project";
  const query = useQuery({
    ...listProjectAssetsApiProjectsProjectIdAssetsGetOptions({
      path: { project_id: isProjectNode ? selectedNode.projectId : "" },
    }),
    enabled: isProjectNode,
    // Used assets are derived from the project's designs, so refining a design
    // elsewhere can change this list. Refetch on every mount (e.g. navigating
    // back to the gallery) so derived assets appear without a hard refresh.
    refetchOnMount: "always",
  });
  return {
    projectAssets: query.data ?? [],
    isProjectAssetsLoading: query.isFetching && query.data === undefined,
    isProjectAssetsError: query.isError && !query.isFetching,
    refetchProjectAssets: query.refetch,
  };
}

function useGalleryUIState() {
  const [isDrawerCollapsed, setIsDrawerCollapsed] = useState(false);
  const [isRootExpanded, setIsRootExpanded] = useState(false);
  const [isOrgRootExpanded, setIsOrgRootExpanded] = useState(false);
  const [expandedProjectIds, setExpandedProjectIds] = useState<Set<string>>(
    () => new Set(),
  );
  return {
    isDrawerCollapsed,
    setIsDrawerCollapsed,
    isRootExpanded,
    setIsRootExpanded,
    isOrgRootExpanded,
    setIsOrgRootExpanded,
    expandedProjectIds,
    setExpandedProjectIds,
  };
}

export type GalleryPageState = ReturnType<typeof useGalleryPage>;

export function useGalleryPage(
  selectedProjectId: string | undefined,
  selectedSessionId: string | undefined,
  view: "assets" | undefined,
  scope: AssetScope | undefined,
) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { orgId } = useAuth();

  const ui = useGalleryUIState();
  const { setIsRootExpanded, setExpandedProjectIds } = ui;

  const {
    activeOrgName,
    canUseOrgFeatures,
    handleShareAssetWithOrg,
    shareAssetFailed,
  } = useAssetsSection(queryClient);

  const tree = useGalleryTreeData(
    orgId,
    { projectId: selectedProjectId, view, scope },
    canUseOrgFeatures,
  );
  const { selectedNode, slimSessions } = tree;

  const assets = useAssets(selectedNode.type === "assets");
  const projectAssets = useProjectAssets(selectedNode);

  const invalidateGallery = () => {
    queryClient.invalidateQueries({
      queryKey: getGalleryTreeApiSessionsGalleryTreeGetQueryKey(),
    });
    queryClient.invalidateQueries({
      queryKey: listSessionsApiSessionsGetQueryKey(),
    });
  };

  const mutations = useGalleryMutations(
    invalidateGallery,
    navigate,
    setIsRootExpanded,
    setExpandedProjectIds,
  );

  const handlers = useGalleryHandlers({
    navigate,
    selectedProjectId,
    selectedNode,
    slimSessions,
    ...mutations,
    setExpandedProjectIds,
  });

  return {
    ...tree,
    ...ui,
    ...handlers,
    activeOrgName,
    canUseOrgFeatures,
    libraryAssets: assets.assets,
    isAssetsLoading: assets.isLoading,
    isAssetsError: assets.isError,
    refetchAssets: assets.refetch,
    ...projectAssets,
    handleShareAssetWithOrg,
    shareAssetFailed,
    selectedProjectId,
    selectedSessionId,
    navigate,
  };
}
